import { Pipe, PipeTransform } from '@angular/core';


export type ValueType
    = 'text'
    | 'price'
    | 'price2'
    | 'date'
    | 'dateY2'
    | 'dateMedium'
    | 'iso-date'
    | 'number'
    | 'percentage1'
    | 'percentage4'
    | 'number2'
    | 'number0-2'
    | 'link'
    | 'links';


const toNumber = <T>(value: T): number | null => {
    if (typeof value !== 'number' && (typeof value !== 'string' || value === '')) {
        return null;
    }
    const num = +value;
    return !isNaN(num) ? num : null;
};


const formatNumber = (format: (_: string) => string, value: number | null, decimals: number, padDecimals?: number) => {
    if (value === null) {
        return null;
    }
    const parts = value.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts[1] && padDecimals) {
        parts[1] = parts[1].padEnd(padDecimals, '0');
    }
    return format(parts.join('.'));
};


const formatYear = (value: string, format: 'date' | 'dateY2') => {
    const places = format === 'dateY2' ? 2 : 4;
    return `${value.substring(4 - places, 4).padStart(places, '0')}`;
};


const formatDateMedium = (year: string, month: string, day: string) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[+month - 1]} ${+day}, ${formatYear(year, 'date')}`;
};


const price = (value: string) => `$${value}`;

const num = (value: string) => value;

const percentage = (value: string) => `${value}%`;


export const formatValue = <T>(value: T, type?: ValueType): T | null | string => {
    switch (type) {
        case 'price':
            return formatNumber(price, toNumber(value), 0);
        case 'price2':
            return formatNumber(price, toNumber(value), 2, 2);
        case 'number':
            return formatNumber(num, toNumber(value), 0);
        case 'number2':
            return formatNumber(num, toNumber(value), 2, 2);
        case 'number0-2':
            return formatNumber(num, toNumber(value), 2, 0);
        case 'percentage1':
            return formatNumber(percentage, toNumber(value), 1, 0);
        case 'percentage4':
            return formatNumber(percentage, toNumber(value), 4, 0);
        case 'date':
        case 'dateY2':
        case 'dateMedium':
        case 'iso-date': {
            if (value instanceof Date) {
                value = value.toISOString() as T;
            }
            if (typeof value !== 'string') {
                return null;
            }
            const match = value.match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?/);
            if (!match) {
                return null;
            }
            const [_, $1, $2, $3] = match;
            if (type === 'iso-date') {
                return `${$1.padStart(4, '0')}-${$2.padStart(2, '0')}-${$3.padStart(2, '0')}`;
            }
            if (type === 'dateMedium') {
                return formatDateMedium($1, $2, $3);
            }
            if (!$2) {
                return formatYear($1, type);
            }
            if (!$3) {
                return `${$2}/${formatYear($1, type)}`;
            }
            return `${$3.padStart(2, '0')}/${$2.padStart(2, '0')}/${formatYear($1, type)}`;
        }
        case 'link':
            if (!value || typeof value !== 'object' || !('name' in value)) {
                return value;
            }
            if ('link' in value) {
                if (value.link === null) {
                    return `${value.name}`;
                } else {
                    return `<a href="${value.link}" target="_blank">${value.name}</a>`;
                }
            }
            return value.name as T;
        case 'links':
            if (!value || !Array.isArray(value)) {
                return value;
            }
            return value.map((v: unknown) => formatValue(v, 'link')).join(', ');
        default:
            return value;
    }
};


@Pipe({
    name: 'value',
})
export class ValuePipe implements PipeTransform {

    transform(value: unknown, type?: ValueType) {
        return formatValue(value, type);
    }
}
